import './app.css';

import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, HeadContent, Outlet, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { Suspense } from 'react';

import { CadstromColorProviderContextProvider } from '#components/cad-renderer/context/cadstrom-color-provider-context-provider.js';
import { CommandBarProvider } from '#components/command-bar/command-bar-provider.js';
import { GlobalCommandBar } from '#components/command-bar/command-bar.js';
import { CommandBarGlobals } from '#components/command-bar/providers/command-bar-globals.js';
import { ActiveOrgComplianceGate } from '#components/compliance-banner/active-org-compliance-gate.js';
import { DatasheetPopover } from '#components/datasheet-popover/datasheet-popover.js';
import { Loading } from '#components/loading.js';
import { ResizablePanel } from '#components/resizable-panel/resizable-panel.js';
import { GenericErrorBoundary } from '#screens/error-boundaries/generic-error-boundary.js';
import { AbilityContextProvider } from '#state/contexts/ability-context-provider.js';
import { isAuth0CallbackPathname } from '#utils/auth0-callback-route.js';
import { AppBreadcrumbs } from './app-breadcrumbs.js';
import { Sidebar } from './nav-bar/sidebar/sidebar.js';
import { showDevToolsAtom } from './show-dev-tools-atom.js';
import { ToastStack } from './toast-stack.js';
import { useInitializeCurrentOrganization } from './use-initialize-current-organization.js';

import type { MongoAbility } from '@casl/ability';
import type { RouterContext } from '#src/entry-client.js';

export const Route = createRootRouteWithContext<RouterContext>()({
	head: () => ({ meta: [{ title: 'Cadstrom' }] }),
	component: RootLayout,
});

function RootLayout() {
	const isAuthCallback = useRouterState({
		select: (state) => isAuth0CallbackPathname(state.location.pathname),
	});

	// While the Auth0 callback route exchanges the auth code and provisions the
	// account, no usable session or access token exists yet. Mounting the
	// authenticated shell here would race its auth-dependent queries (ability
	// context, current-org init, compliance gate) against provisioning and
	// surface transient "missing refresh token" / 401 error pages, so render a
	// bare outlet until the callback redirects onward.
	if (isAuthCallback) {
		return (
			<>
				<HeadContent />
				<Suspense fallback={<Loading />}>
					<Outlet />
				</Suspense>
			</>
		);
	}

	return <AuthenticatedRootLayout />;
}

function AuthenticatedRootLayout() {
	useInitializeCurrentOrganization();

	const [showDevTools, setShowDevTools] = useAtom(showDevToolsAtom);

	return (
		<>
			<HeadContent />

			<AbilityContextProvider>
				<CommandBarProvider>
					<CadstromColorProviderContextProvider>
						{/* Global command bar elements */}
						<GlobalCommandBar />
						<CommandBarGlobals />

						<div
							id={RootLayout.name}
							className={clsx(
								'flex grow flex-col bg-linear-to-bl contain-layout',
								'from-background to-[color-mix(in_oklab,var(--color-background),black_10%)]',
								'light:from-background light:to-[color-mix(in_oklab,var(--color-background),var(--color-blue-9)_10%)]',
							)}
						>
							<div className='flex min-h-0 grow items-stretch'>
								<Sidebar />
								<div
									id={`${RootLayout.name}-outletWrapper`}
									className='isolate flex min-h-0 grow flex-col contain-size'
								>
									<ActiveOrgComplianceGate />
									<AppBreadcrumbs />
									<GenericErrorBoundary>
										<Suspense fallback={<Loading />}>
											<Outlet />
										</Suspense>
									</GenericErrorBoundary>
								</div>
							</div>

							<ToastStack />
						</div>

						<ResizablePanel
							position='bottom'
							initialSize='20rem'
							isCollapsed={!showDevTools}
							onIsCollapsedChange={() => setShowDevTools(null)}
							id={`${RootLayout.name}-devToolsPanel`}
							className={clsx('sticky bottom-0 isolate', showDevTools)}
						>
							{showDevTools === 'tanstackRouter' ?
								<TanStackRouterDevtoolsPanel
									// The Devtools don't have a set intrinsic height, making them too tall, so I have to set `min-h-0`
									// here -- also, they come wrapped in an extra `<div>`, which I had to target using a `:has` selector.
									// Also, this is probably a TanStack Router Devtools bug, but they use a variable called
									// `--tsrd-font-size` throughout the markup, but they don't actually set it anywhere. So I'm setting it
									// here manually. I have to set it larger than `--text-base` because they set their own `font-size` to be
									// 0.75x `--tsrd-font-size` for whatever reason.
									className='h-full min-h-0 [--tsrd-font-size:var(--text-2xl)] [:has(>&)]:h-full [:has(>&)]:min-h-0'
									setIsOpen={() => setShowDevTools(null)}
								/>
							:	null}
							{showDevTools === 'tanstackQuery' ?
								<ReactQueryDevtoolsPanel style={{ height: '100%' }} onClose={() => setShowDevTools(null)} />
							:	null}
						</ResizablePanel>

						<DatasheetPopover />
					</CadstromColorProviderContextProvider>
				</CommandBarProvider>
			</AbilityContextProvider>
		</>
	);
}

declare module '@tanstack/react-router' {
	interface StaticDataRouteOption {
		authorize?: (ability: MongoAbility) => boolean;
	}
}
